/**
 * A PDF.js "image" hit is a paint operation, NOT necessarily a movable object.
 * Avoid editing fragments of composite artwork (e.g. a green circular badge
 * painted behind its number and icon). Never silently destroy other paint.
 */
const EPSILON = 0.15;

export function rectanglesIntersect(a, b) {
  if (!a || !b) return false;
  return Math.max(a.x, b.x) < Math.min(a.x + a.width, b.x + b.width) &&
    Math.max(a.y, b.y) < Math.min(a.y + a.height, b.y + b.height);
}

export function overlapRatio(a, b) {
  if (!rectanglesIntersect(a, b)) return 0;
  const area = (Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x,b.x)) *
    (Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y,b.y));
  return area / Math.max(1, Math.min(a.width*a.height, b.width*b.height));
}

export function sourceCandidateRisk(candidate, others = [], textRuns = []) {
  if (candidate.imageMask) return 'PDF görüntü maskesi tek başına taşınabilecek bir görsel değildir.';
  // Image XObjects and PDF text can be composited into a single printed badge.
  // Removing only one paint rectangle would destroy the other parts.
  if (others.some(other => other.id !== candidate.id && overlapRatio(candidate, other) > 0.07)) {
    return 'Bu görsel başka PDF görüntü katmanlarıyla örtüşüyor; tek nesne değildir.';
  }
  if (textRuns.some(run => overlapRatio(candidate,run)>0.07)) {
    return 'Bu görselin üstünde PDF metni bulunuyor; grup birlikte düzenlenmelidir.';
  }
  return null;
}

export function sourceTextRisk(run, images = []) {
  return images.some(image => overlapRatio(run,image)>0.07)
    ? 'Bu metin bir PDF grafiğinin üzerinde bulunuyor; ayrı silinmesi çevresini bozabilir.'
    : null;
}

/** A composited image can be covered in-place without touching source streams.
 * Group mode is an explicit opt-in; it still must pass local background repair.
 */
export function isInPlaceCover(item) {
  return item?.type === 'image' && !!item.originalRegion && item.compositeMode === 'cover';
}
export function isCompositeGroup(item) {
  return item?.type === 'image' && !!item.originalRegion && item.compositeMode === 'group';
}
export function canEditComposite(item) {
  return isInPlaceCover(item) || isCompositeGroup(item);
}
export function validateInPlaceCover(item) {
  if (!isInPlaceCover(item) || !item.imageDirty) return;
  const region = item.originalRegion;
  if (!item.bytes?.byteLength) throw new Error('Yerinde değiştirme için PNG/JPG görseli seçin.');
  if (['x', 'y', 'width', 'height'].some(key => Math.abs(item[key]-region[key])>0.15)) {
    throw new Error('Yerinde değiştirme modunda konum ve boyut sabit kalmalıdır. Grup olarak taşı seçeneğini kullanın.');
  }
  if ((item.opacity ?? 1) < 0.999) throw new Error('Yerinde değiştirme işleminde görsel tamamen opak olmalıdır.');
}

export function needsSourceReplacement(item) {
  if (!item?.originalRegion || isInPlaceCover(item)) return false;
  if (item.removedSource) return true;
  if (item.hidden) return false;
  if (item.editDirty || item.imageDirty) return true;
  const a = item.originalRegion;
  return ['x','y','width','height'].some(field => Math.abs((item[field] ?? a[field]) - a[field]) > EPSILON) ||
    (item.type === 'image' && (item.opacity ?? 1) < 0.999);
}

export function shouldDrawOverlay(item) {
  if (!item || item.hidden) return false;
  // The original PDF already displays this object until the first real edit.
  // Drawing its snapshot at selection time creates a duplicate / bold ghost.
  if (isInPlaceCover(item)) return !!item.imageDirty;
  return !item.originalRegion || needsSourceReplacement(item);
}

export function replacementRegions(overlays = []) {
  return overlays.filter(needsSourceReplacement).map(item => ({item, region:item.originalRegion}));
}

export function assertNonOverlappingReplacements(overlays = []) {
  for (const item of overlays) validateInPlaceCover(item);
  const regions = replacementRegions(overlays);
  for (const {item} of regions) {
    if (item.sourceRisk && !isCompositeGroup(item)) throw new Error(`${item.sourceRisk} Orijinal PDF'yi korumak için bu değişiklik dışa aktarılamaz.`);
  }
  for (let i = 0; i < regions.length; i++) {
    for (let j = i + 1; j < regions.length; j++) {
      if (rectanglesIntersect(regions[i].region, regions[j].region)) {
        throw new Error('Birbirinin üstüne gelen kaynak PDF alanları ayrı ayrı onarılamaz. Bu düzenlemeleri geri alın veya başka bir yöntem kullanın.');
      }
    }
  }
  return regions;
}
